import React, { useEffect, useState } from "react";
import { TrendingUp, MousePointerClick, Inbox, Megaphone, Copy, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATS = ["Laptop", "Desktop", "Monitor", "Display", "Storage", "Printer", "UPS", "Tablet", "Networking", "Office"];

export default function AdminInsights({ adminKey }) {
  const [data, setData] = useState({ total: 0, items: [] });
  const [daily, setDaily] = useState([]);
  const [sources, setSources] = useState({ total: 0, items: [] });
  const [campaigns, setCampaigns] = useState({ total: 0, items: [] });
  const [loading, setLoading] = useState(true);
  const [builder, setBuilder] = useState({ cat: "Laptop", name: "" });

  useEffect(() => {
    const h = { headers: { "X-Admin-Key": adminKey } };
    Promise.all([
      fetch(`${API}/product_clicks/top`, h).then((r) => r.json()).catch(() => null),
      fetch(`${API}/product_clicks/daily?days=14`, h).then((r) => r.json()).catch(() => null),
      fetch(`${API}/product_clicks/sources`, h).then((r) => r.json()).catch(() => null),
      fetch(`${API}/product_clicks/campaigns`, h).then((r) => r.json()).catch(() => null),
    ]).then(([top, dly, src, camp]) => {
      setData(top && top.items ? top : { total: 0, items: [] });
      setDaily(dly && dly.series ? dly.series : []);
      setSources(src && src.items ? src : { total: 0, items: [] });
      setCampaigns(camp && camp.items ? camp : { total: 0, items: [] });
    }).finally(() => setLoading(false));
  }, [adminKey]);

  const campaignTag = builder.name ? builder.name.trim().replace(/\s+/g, "_") : "";
  const adLink = `${window.location.origin}/stock/${encodeURIComponent(builder.cat)}${campaignTag ? `?c=${encodeURIComponent(campaignTag)}` : ""}`;
  const posterLink = `${window.location.origin}/poster/${encodeURIComponent(builder.cat)}${campaignTag ? `?c=${encodeURIComponent(campaignTag)}` : ""}`;
  const [copiedWhich, setCopiedWhich] = useState("");
  const copy = async (text, which) => {
    try { await navigator.clipboard.writeText(text); setCopiedWhich(which); setTimeout(() => setCopiedWhich(""), 1800); } catch (err) { console.debug("Copy failed", err); }
  };

  const SRC_COLORS = ["var(--green-800)", "var(--orange-500)", "var(--yellow-500)", "var(--muted)"];
  const max = data.items.reduce((m, i) => Math.max(m, i.clicks), 0) || 1;
  const dMax = daily.reduce((m, d) => Math.max(m, d.count), 0) || 1;
  const dayLabel = (iso) => {
    const dt = new Date(iso + "T00:00:00");
    return dt.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  };

  return (
    <div>
      <div className="mb-6 grid grid-cols-2 gap-4 sm:max-w-md">
        <div className="rounded-2xl border bg-white px-5 py-5" style={{ borderColor: "var(--line)", boxShadow: "0 8px 20px rgba(45,106,79,0.05)" }}>
          <div className="flex items-center gap-2 font-heading text-[30px] font-extrabold" style={{ color: "var(--orange-700)" }}>
            <MousePointerClick size={24} /> {data.total}
          </div>
          <div className="mt-0.5 text-[13px] font-semibold uppercase tracking-wide" style={{ color: "var(--muted)" }}>Total enquiry clicks</div>
        </div>
        <div className="rounded-2xl border bg-white px-5 py-5" style={{ borderColor: "var(--line)", boxShadow: "0 8px 20px rgba(45,106,79,0.05)" }}>
          <div className="flex items-center gap-2 font-heading text-[30px] font-extrabold" style={{ color: "var(--green-900)" }}>
            <TrendingUp size={24} /> {data.items.length}
          </div>
          <div className="mt-0.5 text-[13px] font-semibold uppercase tracking-wide" style={{ color: "var(--muted)" }}>Products enquired</div>
        </div>
      </div>

      <h3 className="mb-3 font-heading text-[18px] font-extrabold" style={{ color: "var(--green-950)" }}>Enquiry clicks — last 14 days</h3>
      <div className="mb-8 rounded-2xl border bg-white p-5" style={{ borderColor: "var(--line)", boxShadow: "0 8px 20px rgba(45,106,79,0.05)" }}>
        {daily.length === 0 ? (
          <p className="text-[13.5px]" style={{ color: "var(--muted)" }}>No data yet.</p>
        ) : (
          <div className="no-scrollbar overflow-x-auto">
            <div className="flex min-w-[420px] items-end gap-2" style={{ height: 160 }}>
              {daily.map((d) => (
                <div key={d.date} className="group flex flex-1 flex-col items-center justify-end gap-2">
                  <span className="text-[11px] font-bold" style={{ color: d.count ? "var(--orange-700)" : "transparent" }}>{d.count}</span>
                  <div className="w-full rounded-t-md transition-all"
                       style={{ height: `${Math.max(4, (d.count / dMax) * 118)}px`,
                                background: d.count ? "linear-gradient(180deg, var(--orange-500), var(--orange-700))" : "var(--green-100)" }}
                       title={`${d.count} clicks`} />
                  <span className="whitespace-nowrap text-[10px] font-semibold" style={{ color: "var(--muted)" }}>{dayLabel(d.date)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <h3 className="mb-3 font-heading text-[18px] font-extrabold" style={{ color: "var(--green-950)" }}>Where clicks come from</h3>
      <div className="mb-8 rounded-2xl border bg-white p-5" style={{ borderColor: "var(--line)", boxShadow: "0 8px 20px rgba(45,106,79,0.05)" }}>
        {sources.total === 0 ? (
          <p className="text-[13.5px]" style={{ color: "var(--muted)" }}>No data yet.</p>
        ) : (
          <div className="grid gap-3.5">
            {sources.items.map((s, i) => {
              const pct = Math.round((s.count / sources.total) * 100);
              return (
                <div key={s.label}>
                  <div className="mb-1 flex items-center justify-between text-[13.5px] font-semibold">
                    <span style={{ color: "var(--green-950)" }}>{s.label}</span>
                    <span style={{ color: "var(--muted)" }}>{s.count} · {pct}%</span>
                  </div>
                  <div className="h-2.5 w-full overflow-hidden rounded-full" style={{ background: "var(--green-100)" }}>
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: SRC_COLORS[i % SRC_COLORS.length] }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <h3 className="mb-3 flex items-center gap-2 font-heading text-[18px] font-extrabold" style={{ color: "var(--green-950)" }}>
        <Megaphone size={18} /> Ad campaign tags
      </h3>
      <div className="mb-4 rounded-2xl border bg-white p-5" style={{ borderColor: "var(--line)", boxShadow: "0 8px 20px rgba(45,106,79,0.05)" }}>
        <p className="mb-3 text-[13.5px]" style={{ color: "var(--muted)" }}>Build a tagged link for an ad. Enquiries from it show up under this campaign name below.</p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="grid gap-1 font-heading text-[12.5px] font-semibold" style={{ color: "var(--green-950)" }}>
            Category
            <select value={builder.cat} onChange={(e) => setBuilder({ ...builder, cat: e.target.value })}
                    className="h-10 rounded-lg border bg-white px-3 text-[14px] outline-none" style={{ borderColor: "#d8c8af" }}>
              {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="grid gap-1 font-heading text-[12.5px] font-semibold" style={{ color: "var(--green-950)" }}>
            Campaign name
            <input value={builder.name} onChange={(e) => setBuilder({ ...builder, name: e.target.value })} placeholder="e.g. fb_july_laptops"
                   className="h-10 w-56 rounded-lg border bg-white px-3 text-[14px] outline-none" style={{ borderColor: "#d8c8af" }} />
          </label>
          <div className="flex items-end gap-2">
            <button onClick={() => copy(adLink, "ad")} className="inline-flex h-10 items-center gap-2 rounded-full px-4 font-heading text-[13.5px] font-bold text-white"
                    style={{ background: "linear-gradient(135deg, var(--green-800), var(--green-950))" }}>
              {copiedWhich === "ad" ? <><Check size={15} /> Copied</> : <><Copy size={15} /> Ad link</>}
            </button>
            <button onClick={() => copy(posterLink, "poster")} className="inline-flex h-10 items-center gap-2 rounded-full border-2 px-4 font-heading text-[13.5px] font-bold"
                    style={{ borderColor: "var(--green-800)", color: "var(--green-900)" }}>
              {copiedWhich === "poster" ? <><Check size={15} /> Copied</> : <><Copy size={15} /> Poster link</>}
            </button>
          </div>
        </div>
        <code className="mt-3 block truncate rounded-lg px-3 py-2 text-[13px]" style={{ background: "var(--green-100)", color: "var(--green-900)" }}>{adLink}</code>
        <code className="mt-2 block truncate rounded-lg px-3 py-2 text-[13px]" style={{ background: "var(--cream)", color: "var(--green-900)" }}>{posterLink}</code>
      </div>
      <div className="mb-8 rounded-2xl border bg-white p-5" style={{ borderColor: "var(--line)", boxShadow: "0 8px 20px rgba(45,106,79,0.05)" }}>
        {campaigns.items.length === 0 ? (
          <p className="text-[13.5px]" style={{ color: "var(--muted)" }}>No campaign clicks yet. Share a tagged link to see results here.</p>
        ) : (
          <div className="grid gap-2.5">
            {campaigns.items.map((c) => {
              const pct = campaigns.total ? Math.round((c.count / campaigns.total) * 100) : 0;
              return (
                <div key={c.campaign} className="flex items-center justify-between gap-3">
                  <span className="truncate font-heading text-[14px] font-bold" style={{ color: c.campaign === "Direct / untagged" ? "var(--muted)" : "var(--green-950)" }}>{c.campaign}</span>
                  <span className="flex-shrink-0 text-[13px] font-semibold" style={{ color: "var(--orange-700)" }}>{c.count} · {pct}%</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <h3 className="mb-3 font-heading text-[18px] font-extrabold" style={{ color: "var(--green-950)" }}>Most-enquired products</h3>

      {loading ? (
        <p style={{ color: "var(--muted)" }}>Loading…</p>
      ) : data.items.length === 0 ? (
        <div className="grid place-items-center gap-3 rounded-2xl border bg-white py-16 text-center" style={{ borderColor: "var(--line)" }}>
          <Inbox size={38} style={{ color: "var(--muted)" }} />
          <p className="font-heading font-bold" style={{ color: "var(--green-950)" }}>No enquiry clicks yet</p>
          <p className="text-[13.5px]" style={{ color: "var(--muted)" }}>When visitors tap "Enquire" on a product, it will show up here.</p>
        </div>
      ) : (
        <div className="grid gap-2.5">
          {data.items.map((it, i) => (
            <div key={(it.product_id || "") + i} className="rounded-xl border bg-white p-3.5" style={{ borderColor: "var(--line)" }}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="grid h-6 w-6 place-items-center rounded-md text-[12px] font-extrabold" style={{ background: "var(--green-100)", color: "var(--green-800)" }}>{i + 1}</span>
                    <span className="truncate font-heading text-[14.5px] font-bold" style={{ color: "var(--green-950)" }}>{it.product_name}</span>
                  </div>
                  <span className="mt-0.5 block text-[12px]" style={{ color: "var(--muted)" }}>{[it.brand, it.category].filter(Boolean).join(" · ")}</span>
                </div>
                <span className="flex-shrink-0 font-heading text-[16px] font-extrabold" style={{ color: "var(--orange-700)" }}>{it.clicks}</span>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full" style={{ background: "var(--green-100)" }}>
                <div className="h-full rounded-full" style={{ width: `${(it.clicks / max) * 100}%`, background: "linear-gradient(90deg, var(--orange-700), var(--orange-500))" }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
